/**
 * The Lanes body of a project tab (docs/ux/project-tab-spec.md §1.4–1.7, §1.10–1.12; layout per
 * docs/ux/review/project-tab-refinement.md §2): the composer across the top, then two tracks —
 * L: Needs you and "Your requests"; R: the lanes board and the landing/release
 * strip. Zero states collapse (never a frame of zeros) and idle lanes fold into one line.
 * Read-only over existing data; every indicator opens the inspector (`onInspect(key)`).
 */

import { Link } from '@tanstack/react-router'
import { LANE_COLUMNS, LANE_STATE_LABEL, StatusIcon } from '@viko/transcript-ui'
import { CloudOff, Zap } from 'lucide-react'
import { Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { stripWaiting } from '../../lib/goal-tree.js'
import { homeHref, projectHref, runHref } from '../../lib/ia.js' // ia-shell
import {
  columnTotals,
  foldIdleLanes,
  type LanesHeadPart,
  lanesHeadParts,
} from '../../lib/lanes-layout.js'
import type {
  AskRow,
  Lane,
  LaneAgent,
  LaneCard,
  LaneChip,
  LanesModel,
} from '../../lib/lanes-model.js'
import { isTyping } from '../../lib/shortcut.js'
import { KeyHints, Keys } from '../Keys.js'
import { RowActionBar, useRowActions } from '../RowActions.js'
import { AsksHead } from './asks-head.js'
import { Glyph, LANE_GLYPH } from './glyphs.js'
import { ReleaseStrip } from './ReleaseStrip.js'
import { SelectedKeyContext, useIsSelected } from './selection.js'
import type { LanesSlots } from './slots.js'
import './lanes.css'

export interface LanesViewProps {
  model: LanesModel
  slots: LanesSlots
  onInspect: (key: string) => void
  /** Open inspector key (`task:<id>`, …), for the bark box's direct-to-run chip. */
  inspectKey?: string
  /** Start a queued task now (existing Start session sheet). */
  onStart?: (card: LaneCard) => void
  /** When the hub disconnected: live lines are "as of" this time. */
  frozenAt?: number
  /** Starter text for the composer, when the host owns it (the goal inspector's starters). */
  prefill?: { text: string; nonce: number }
  /** Put starter text in the composer (host-owned); without it the view keeps its own. */
  onStarter?: (text: string) => void
  /** Work outside Viko waiting for review: one row in the Needs you section. */
  outside?: ReactNode
}

const MAX_CARDS = 30

export function LanesView({
  model,
  slots,
  onInspect,
  inspectKey,
  onStart,
  frozenAt,
  prefill: hostPrefill,
  onStarter,
  outside,
}: LanesViewProps) {
  const [ownPrefill, setOwnPrefill] = useState<{ text: string; nonce: number }>()
  const prefill = hostPrefill ?? ownPrefill
  const starter = onStarter ?? ((text: string) => setOwnPrefill({ text, nonce: Date.now() }))
  const inspectRunId = useMemo(() => {
    if (!inspectKey?.startsWith('task:')) return undefined
    const id = inspectKey.split(':')[1]
    return model.cards.find((c) => c.id === id && c.agent)?.runId
  }, [inspectKey, model.cards])
  const announce = useAnnouncer(model)
  useLaneKeys()
  const fallbackAsks = <AsksPanel model={model} onInspect={onInspect} outside={outside} />

  return (
    <SelectedKeyContext.Provider value={inspectKey}>
      <div className="lk-body" data-lanes-state={model.state}>
        <Degraded model={model} {...(frozenAt ? { frozenAt } : {})} />
        <div className="lk-dock">
          <slots.BarkBox
            projectId={model.projectId}
            projectName={model.name}
            size={model.state === 'empty' ? 'large' : 'normal'}
            disabled={model.locked}
            {...(prefill ? { prefill } : {})}
            {...(inspectRunId ? { inspectRunId } : {})}
          />
        </div>
        <div className="lk-grid">
          <div className="lk-left">
            <div className="lk-asks-wrap">
              {/* now-inbox: the same rows as the Inbox (quick-pick, sessions, assumptions) */}
              {slots.AsksPanel ? (
                <slots.AsksPanel
                  projectId={model.projectId}
                  {...(inspectKey ? { inspectKey } : {})}
                  askIds={model.asks.map((a) => a.id)}
                  onInspect={onInspect}
                  outside={outside}
                  fallback={fallbackAsks}
                />
              ) : (
                fallbackAsks
              )}
            </div>
            <div className="lk-feed">
              <slots.OutcomeFeed
                projectId={model.projectId}
                projectName={model.name}
                limit={3}
                onInspect={onInspect}
              />
            </div>
          </div>
          <div className="lk-right">
            <LanesSection
              model={model}
              onInspect={onInspect}
              onStart={onStart}
              onStarter={starter}
            />
            <div className="lk-builds-wrap">
              <ReleaseStrip
                rows={model.builds}
                projectId={model.projectId}
                onInspect={onInspect}
                aside={
                  <Link className="lk-head-link" to={projectHref(model.projectId, 'releases')}>
                    All builds <Keys k="g b" />
                  </Link>
                }
              />
            </div>
          </div>
        </div>
        <div className="sr-only" aria-live="polite" role="status">
          {announce}
        </div>
      </div>
    </SelectedKeyContext.Provider>
  )
}

// ---- asks ---------------------------------------------------------------------------------------

function AsksPanel({
  model,
  onInspect,
  outside,
}: {
  model: LanesModel
  onInspect: (k: string) => void
  outside?: ReactNode
}) {
  const { asks } = model
  return (
    <section className="lk-asks" aria-labelledby="lk-asks-h">
      <AsksHead
        projectId={model.projectId}
        blocking={model.counts.needs}
        rows={asks.length}
        action={
          asks.length > 1 ? (
            <Link
              className="vk-btn vk-btn--secondary vk-btn--sm"
              to={homeHref({ v: 'needs', project: model.projectId })}
            >
              <span>
                {asks.length === model.counts.needs
                  ? `Decide all ${asks.length}`
                  : `Review all ${asks.length} in Home`}
              </span>
            </Link>
          ) : undefined
        }
      />
      {asks.length ? (
        <ol className="lk-asks__list">
          {asks.map((a) => (
            <AskItem key={a.id} ask={a} onInspect={onInspect} />
          ))}
        </ol>
      ) : null}
      {outside}
    </section>
  )
}

function AskItem({ ask, onInspect }: { ask: AskRow; onInspect: (k: string) => void }) {
  const open = () => onInspect(`ask:${encodeURIComponent(ask.id)}`)
  return (
    <li className="lk-ask" data-ask={ask.id}>
      <button type="button" className="lk-ask__q" onClick={open}>
        <Glyph kind={ask.blocking ? 'wait' : 'idle'} />
        <span>
          {ask.locked ? 'Encrypted: unlock this browser to read' : ask.question || ask.title}
        </span>
      </button>
      <div className="lk-chips">
        {ask.leverage.map((l, i) => (
          <button
            key={l}
            type="button"
            className={`lk-chip${i === 0 ? ' is-strong' : ''}`}
            onClick={open}
          >
            {l}
          </button>
        ))}
        {ask.workstreamName ? <span className="lk-chip">{ask.workstreamName}</span> : null}
      </div>
      {ask.options.length ? (
        <ul className="lk-opts" aria-label="Options">
          {ask.options.map((o) => (
            <li key={o.label} className={`lk-opt${o.suggested ? ' is-suggested' : ''}`}>
              {o.label}
              {o.suggested ? <small>Suggested</small> : null}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="lk-ask__acts">
        <button type="button" className="vk-btn vk-btn--secondary vk-btn--sm" onClick={open}>
          <span>Details</span>
        </button>
        <Link className="lk-link" to={ask.runId ? runHref(ask.runId) : homeHref({ v: 'needs' })}>
          {ask.runId ? 'Answer in the conversation' : 'Answer in Needs you'}
        </Link>
      </div>
    </li>
  )
}

// ---- lanes section --------------------------------------------------------------------------------

const STARTERS = [
  ['Find and fix flaky tests', 'Find and fix the flaky tests'],
  ['Test builds to my phone', 'Set up test builds to my phone'],
] as const

const BOARD_HINTS: [string, string][] = [
  ['j k', 'move'],
  ['⏎', 'open'],
]

function LanesSection({
  model,
  onInspect,
  onStart,
  onStarter,
}: {
  model: LanesModel
  onInspect: (k: string) => void
  onStart?: ((c: LaneCard) => void) | undefined
  onStarter: (text: string) => void
}) {
  const parts = lanesHeadParts(model.counts)
  const [layout, setLayout] = useLanesLayout()
  return (
    <section className="lk-lanes" aria-labelledby="lk-lanes-h">
      <div className="lk-section-h">
        <h2 id="lk-lanes-h">Lanes</h2>
        {parts.length ? (
          <p className="lk-head-sentence">
            {parts.map((p, i) => (
              <Fragment key={p.key}>
                {i ? <span aria-hidden="true"> · </span> : null}
                <HeadPart part={p} projectId={model.projectId} onInspect={onInspect} />
              </Fragment>
            ))}
          </p>
        ) : null}
        {model.lanes.length ? (
          <span className="lk-section-h__act lk-lhead-acts">
            {layout === 'board' && model.cards.length ? (
              <KeyHints label="Lane keys" hints={BOARD_HINTS} />
            ) : null}
            <button
              type="button"
              className="lk-layout-btn"
              aria-pressed={layout === 'board'}
              aria-keyshortcuts="Shift+B"
              onClick={() => setLayout(layout === 'board' ? 'rows' : 'board')}
            >
              <Keys k="⇧B" />
              <span>{layout === 'board' ? 'rows' : 'board'}</span>
            </button>
          </span>
        ) : null}
      </div>
      {model.capacity ? (
        <CapacityLine model={model} onInspect={onInspect} onStart={onStart} />
      ) : null}
      {model.lanes.length ? (
        layout === 'board' ? (
          <Board model={model} onInspect={onInspect} frozen={model.degraded.hub} />
        ) : (
          <LaneRows model={model} onInspect={onInspect} frozen={model.degraded.hub} />
        )
      ) : (
        <div className="lk-nolanes">
          <p className="lk-muted">No lanes yet. What you ask becomes lanes.</p>
          <div className="lk-starters">
            {STARTERS.map(([label, text]) => (
              <button key={label} type="button" className="lk-chip" onClick={() => onStarter(text)}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function HeadPart({
  part: p,
  projectId,
  onInspect,
}: {
  part: LanesHeadPart
  projectId: string
  onInspect: (k: string) => void
}) {
  const text = `${p.n} ${p.label}`
  if (p.key === 'running')
    return (
      <Link className="lk-head-num" to={homeHref({ v: 'running', project: projectId })}>
        {text}
      </Link>
    )
  return (
    <button
      type="button"
      className="lk-head-num"
      onClick={() => onInspect(p.key === 'queued' ? 'capacity' : 'landed')}
    >
      {text}
    </button>
  )
}

function CapacityLine({
  model,
  onInspect,
  onStart,
}: {
  model: LanesModel
  onInspect: (k: string) => void
  onStart?: ((c: LaneCard) => void) | undefined
}) {
  const c = model.capacity
  if (!c) return null
  const first = c.picks[0]
  return (
    <p className="lk-capline">
      <Zap size={14} aria-hidden="true" />
      <button type="button" className="lk-head-num" onClick={() => onInspect('capacity')}>
        {c.text}
      </button>
      {first && onStart ? (
        <button type="button" className="lk-link" onClick={() => onStart(first)}>
          Start {first.title}
        </button>
      ) : null}
    </p>
  )
}

function Degraded({ model, frozenAt }: { model: LanesModel; frozenAt?: number }) {
  if (!model.degraded.hub && !model.degraded.machines.length) return null
  return (
    <div className="lk-degraded">
      {model.degraded.hub ? (
        <div className="lk-banner" role="status">
          <CloudOff size={15} aria-hidden="true" />
          <span>
            Disconnected from Viko. Live lines are frozen
            {frozenAt ? (
              <>
                {' '}
                as of{' '}
                {new Date(frozenAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </>
            ) : null}
            ; what you send waits until you are back.
          </span>
        </div>
      ) : null}
      {model.degraded.machines.length ? (
        <div className="lk-banner" role="status">
          <Glyph kind="idle" />
          <span>
            {model.degraded.machines.join(' and ')}{' '}
            {model.degraded.machines.length === 1 ? 'is' : 'are'} offline. Lanes that run there wait
            until {model.degraded.machines.length === 1 ? 'it is' : 'they are'} back.
          </span>
        </div>
      ) : null}
    </div>
  )
}

// ---- board --------------------------------------------------------------------------------------

/** Lanes view: rows (default) or the board (`B`), remembered per browser (app-clarity WP-4). */
export type LanesLayout = 'rows' | 'board'
const LAYOUT_KEY = 'viko.lanes.layout'
function storedLayout(): LanesLayout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === 'board' ? 'board' : 'rows'
  } catch {
    return 'rows'
  }
}

/** The lanes layout (rows by default, the board on ⇧B), remembered in this browser. */
function useLanesLayout(): [LanesLayout, (l: LanesLayout) => void] {
  const [layout, setLayoutState] = useState<LanesLayout>(storedLayout)
  const setLayout = (l: LanesLayout) => {
    setLayoutState(l)
    try {
      localStorage.setItem(LAYOUT_KEY, l)
    } catch {
      // private window: this page load only
    }
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'B' || !e.shiftKey || e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      e.preventDefault()
      setLayoutState((l) => {
        const next = l === 'rows' ? 'board' : 'rows'
        try {
          localStorage.setItem(LAYOUT_KEY, next)
        } catch {}
        return next
      })
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [])
  return [layout, setLayout]
}

/** One row per lane: what it is doing now and its counts; ⏎ (or the chevron) shows its cards. */
function LaneRows({
  model,
  onInspect,
  frozen,
}: {
  model: LanesModel
  onInspect: (k: string) => void
  frozen: boolean
}) {
  const { active, idle } = foldIdleLanes(model.lanes)
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const toggle = (id: string) =>
    setOpen((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const row = (l: Lane, i: number) => {
    const isOpen = open.has(l.id)
    const queued = l.columns.queued.length
    const count = queued
      ? `${queued} queued`
      : l.columns.verifying.length
        ? `${l.columns.verifying.length} checking`
        : l.columns.landed.length
          ? `${l.columns.landed.length} landed today`
          : l.agents.length
            ? ''
            : 'idle'
    return (
      <li key={l.id} className={`lk-lrow${l.hot ? ' is-hot' : ''}`} data-lane-row={l.id}>
        <div className="lk-lrow__main">
          <button
            type="button"
            className="lk-lrow__toggle"
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Hide' : 'Show'} the cards in ${l.name}`}
            onClick={() => toggle(l.id)}
          >
            <span className="lk-lrow__name" data-ellipsis-ok>
              {model.locked ? 'Locked lane' : l.name}
            </span>
          </button>
          <LaneLines lane={l} frozen={frozen} locked={model.locked} onInspect={onInspect} />
          {l.pseudo ? (
            <span className="lk-lrow__count">{count}</span>
          ) : (
            <button
              type="button"
              className="lk-lrow__count"
              aria-label={`Open ${l.name}${count ? `: ${count}` : ''}`}
              onClick={() => onInspect(`ws:${l.id}`)}
            >
              {count || 'Open'}
            </button>
          )}
        </div>
        {isOpen ? (
          <div className="lk-board is-one">
            <LaneRow
              lane={l}
              index={i}
              onInspect={onInspect}
              frozen={frozen}
              locked={model.locked}
            />
          </div>
        ) : null}
      </li>
    )
  }
  return (
    <section className="lk-lrows" aria-label="Lanes, one row each">
      <ul className="lk-lrows__list">{active.map(row)}</ul>
      {idle.length ? (
        <details className="lk-idle">
          <summary>
            <Glyph kind="idle" />
            <span>
              {idle.length} idle {idle.length === 1 ? 'lane' : 'lanes'}
            </span>
          </summary>
          <ul className="lk-lrows__list">{idle.map((l, i) => row(l, active.length + i))}</ul>
        </details>
      ) : null}
    </section>
  )
}

const SUB_LINES = 4
const STATE_WORD: Partial<Record<LaneCard['state'], string>> = {
  queued: 'queued',
  'needs-you': 'needs you',
  blocked: 'blocked',
  verifying: 'checking',
  landing: 'landing',
  landed: 'landed',
}

/** A lane's live line (its agent) and the next few cards, each one line: icon, title, state. */
function LaneLines({
  lane,
  frozen,
  locked,
  onInspect,
}: {
  lane: Lane
  frozen: boolean
  locked: boolean
  onInspect: (k: string) => void
}) {
  const agentTasks = new Set(lane.agents.map((a) => a.taskId).filter(Boolean))
  const cards = [
    ...lane.columns.queued.filter((c) => c.state === 'needs-you' || c.state === 'blocked'),
    ...lane.columns.running.filter((c) => !agentTasks.has(c.id)),
    ...lane.columns.queued.filter((c) => c.state !== 'needs-you' && c.state !== 'blocked'),
    ...lane.columns.verifying,
  ]
  const shown = cards.slice(0, SUB_LINES)
  const more = cards.length - shown.length
  return (
    <div className="lk-lrow__body">
      {lane.agents.map((a) => (
        <button
          key={a.runId}
          type="button"
          className="lk-lline"
          onClick={() => onInspect(`${a.taskId ? `task:${a.taskId}` : `run:${a.runId}`}:progress`)}
        >
          <span className="vk-icol">
            <StatusIcon kind={a.waiting ? 'wait' : 'run'} live={!frozen && !a.waiting} />
          </span>
          {locked ? (
            <span className="lk-skel" />
          ) : (
            <span className="lk-lline__t">
              <span className="lk-lline__title">{a.title}</span>
              <span className="lk-lline__m">
                {a.waiting ? ` needs you · ${stripWaiting(a.line)}` : ` · ${a.label} · ${a.line}`}
              </span>
            </span>
          )}
        </button>
      ))}
      {shown.map((c) => (
        <button
          key={c.id}
          type="button"
          className="lk-lline"
          onClick={() => onInspect(`task:${c.id}`)}
        >
          <span className="vk-icol">
            <StatusIcon kind={LANE_GLYPH[c.state]} live={false} />
          </span>
          {locked ? (
            <span className="lk-skel" />
          ) : (
            <span className="lk-lline__t">
              <span className="lk-lline__title">{c.title}</span>
              <span className="lk-lline__m">
                {' '}
                {c.proposed
                  ? 'proposed · approve to queue'
                  : `${STATE_WORD[c.state] ?? c.state}${c.reason ? ` · ${c.reason}` : ''}`}
              </span>
            </span>
          )}
        </button>
      ))}
      {!lane.agents.length && !shown.length ? (
        <p className="lk-lline is-quiet">
          <span className="vk-icol">
            <StatusIcon kind={lane.columns.landed.length ? 'ok' : 'idle'} />
          </span>
          <span className="lk-lline__m">
            {lane.columns.landed.length
              ? `Landed ${lane.columns.landed.length} today · last: ${lane.columns.landed[0]?.title ?? ''}`
              : (lane.idleReason ?? (lane.paused ? 'Paused' : 'No agent now'))}
          </span>
        </p>
      ) : null}
      {more > 0 ? <p className="lk-lline__more">{more} more in this lane</p> : null}
    </div>
  )
}

function Board({
  model,
  onInspect,
  frozen,
}: {
  model: LanesModel
  onInspect: (k: string) => void
  frozen: boolean
}) {
  const { active, idle } = foldIdleLanes(model.lanes)
  const totals = columnTotals(active)
  const earlier = model.lanes.reduce((n, l) => n + l.earlier.length, 0)
  const ref = useRef<HTMLElement>(null)
  // A column count drills to that column's first card (then j/k walk it).
  const focusColumn = (ci: number) => {
    const first = ref.current?.querySelector<HTMLElement>(`[data-lane-key$=":${ci}:0"]`)
    first?.focus()
    first?.scrollIntoView?.({ block: 'nearest' })
  }
  const row = (l: Lane, i: number) => (
    <LaneRow
      key={l.id}
      lane={l}
      index={i}
      onInspect={onInspect}
      frozen={frozen}
      locked={model.locked}
    />
  )
  return (
    <section
      ref={ref}
      className="lk-board"
      aria-label="Lanes by task state"
      data-lanes-board
      // Empty columns shrink to their head, so cards in the busy columns keep readable widths.
      style={
        {
          '--lk-cols': LANE_COLUMNS.map((c) =>
            totals[c.key] ? 'minmax(160px, 1fr)' : 'minmax(96px, 0.45fr)',
          ).join(' '),
        } as React.CSSProperties
      }
    >
      <div className="lk-board__head">
        <span>Lane</span>
        {LANE_COLUMNS.map((c, ci) => (
          <span key={c.key}>
            {totals[c.key] ? (
              <button
                type="button"
                className="lk-colcount"
                aria-label={`${c.label}: ${totals[c.key]}`}
                onClick={() => focusColumn(ci)}
              >
                {c.label}
                <b>{totals[c.key]}</b>
              </button>
            ) : (
              c.label
            )}
          </span>
        ))}
      </div>
      {active.map(row)}
      {idle.length ? (
        <details className="lk-idle">
          <summary>
            <Glyph kind="idle" />
            <span>
              {idle.length} idle {idle.length === 1 ? 'lane' : 'lanes'}:{' '}
              {idle.map((l) => l.name).join(', ')}
            </span>
          </summary>
          {idle.map((l, i) => row(l, active.length + i))}
        </details>
      ) : null}
      {earlier ? (
        <div className="lk-board__foot">
          <button type="button" className="lk-link" onClick={() => onInspect('landed')}>
            See what landed earlier ({earlier})
          </button>
        </div>
      ) : null}
    </section>
  )
}

function LaneRow({
  lane,
  index,
  onInspect,
  frozen,
  locked,
}: {
  lane: Lane
  index: number
  onInspect: (k: string) => void
  frozen: boolean
  locked: boolean
}) {
  const hid = `lk-lane-${lane.id}`
  return (
    <section
      className={`lk-lane${lane.hot ? ' is-hot' : ''}${lane.collapsed ? ' is-collapsed' : ''}`}
      aria-labelledby={hid}
      data-lane={index}
    >
      <div className="lk-lane__h">
        <h3 id={hid}>
          {locked ? (
            <>
              <span className="lk-skel" />
              <span className="sr-only">Locked lane</span>
            </>
          ) : lane.pseudo ? (
            <span className="lk-lane__t">{lane.name}</span>
          ) : (
            <button type="button" className="lk-lane__t" onClick={() => onInspect(`ws:${lane.id}`)}>
              {lane.name}
            </button>
          )}
        </h3>
        {lane.paused ? <span className="lk-lane__sub">Paused</span> : null}
        {lane.agents.map((a) => (
          <AgentLine
            key={a.runId}
            agent={a}
            onInspect={onInspect}
            frozen={frozen}
            locked={locked}
          />
        ))}
        {!lane.agents.length ? (
          <div className="lk-lane__agent">
            <button
              type="button"
              className="lk-chip"
              onClick={() => onInspect(lane.pseudo ? 'capacity' : `ws:${lane.id}`)}
            >
              <Glyph kind="idle" />
              No agent now
            </button>
            <p className="lk-live is-quiet">{lane.idleReason}</p>
          </div>
        ) : null}
      </div>
      {LANE_COLUMNS.map((c, ci) => {
        const cards = lane.columns[c.key]
        return (
          <div key={c.key} className={`lk-col${cards.length ? '' : ' is-empty'}`}>
            {cards.length ? <h4 className="lk-col__h">{c.label}</h4> : null}
            <ul className="lk-col__list" aria-label={`${lane.name}: ${c.label}, ${cards.length}`}>
              {cards.slice(0, MAX_CARDS).map((card, i) => (
                <TaskCard
                  key={card.id}
                  card={card}
                  lane={index}
                  col={ci}
                  idx={i}
                  onInspect={onInspect}
                  locked={locked}
                />
              ))}
              {cards.length > MAX_CARDS ? (
                <li>
                  <button
                    type="button"
                    className="lk-more"
                    onClick={() => onInspect(`ws:${lane.id}`)}
                  >
                    {cards.length - MAX_CARDS} more in {lane.name}
                  </button>
                </li>
              ) : null}
            </ul>
          </div>
        )
      })}
    </section>
  )
}

const BAD_PHASES = new Set(['stalled', 'looping', 'blocked-environment', 'failed', 'offline'])

function AgentLine({
  agent,
  onInspect,
  frozen,
  locked,
}: {
  agent: LaneAgent
  onInspect: (k: string) => void
  frozen: boolean
  locked: boolean
}) {
  const key = agent.taskId ? `task:${agent.taskId}` : `run:${agent.runId}`
  const acts = useRowActions({ kind: 'run', id: agent.runId, title: agent.label, live: true })
  const tone = frozen
    ? ' is-frozen'
    : agent.waiting
      ? ' is-wait'
      : BAD_PHASES.has(agent.phase)
        ? ' is-bad'
        : agent.streaming
          ? ' is-streaming'
          : ' is-quiet'
  return (
    <div className="lk-lane__agent" data-agent={agent.runId} {...acts.rowProps}>
      <button
        type="button"
        className="lk-chip"
        onClick={() => onInspect(`${key}:progress`)}
        title={`${agent.label} · ${agent.phaseLabel}`}
      >
        <span className={`lk-dot is-${agent.agent}`} aria-hidden="true" />
        {agent.label}
        <span className="sr-only">, {agent.phaseLabel}</span>
      </button>
      <p className={`lk-live${tone}`}>
        <span className="lk-live__beat" aria-hidden="true" />
        {locked ? <span className="lk-skel" /> : <span>{agent.line}</span>}
      </p>
      {locked ? null : <RowActionBar api={acts} label={agent.label} />}
    </div>
  )
}

function TaskCard({
  card,
  lane,
  col,
  idx,
  onInspect,
  locked,
}: {
  card: LaneCard
  lane: number
  col: number
  idx: number
  onInspect: (k: string) => void
  locked: boolean
}) {
  const stateLabel = card.subLabel ?? LANE_STATE_LABEL[card.state]
  const open = (tab = '') => onInspect(`task:${card.id}${tab}`)
  const selected = useIsSelected('task', card.id) // now-inbox: selected state
  const acts = useRowActions(
    locked
      ? undefined
      : {
          kind: 'task',
          id: card.id,
          title: card.title,
          ...(card.runId ? { runId: card.runId } : {}),
        },
  )
  return (
    <li>
      <article
        className={`lk-card is-${card.state}${selected ? ' is-selected' : ''}`}
        data-card={card.id}
        {...acts.rowProps}
      >
        <button
          type="button"
          aria-current={selected ? 'true' : undefined}
          className="lk-card__title"
          data-lane-key={`${lane}:${col}:${idx}`}
          aria-label={`${locked ? 'Locked task' : card.title}, ${stateLabel}, ${card.laneName}`}
          onClick={() => open()}
        >
          <Glyph kind={LANE_GLYPH[card.state]} />
          {locked ? <span className="lk-skel" /> : <span>{card.title}</span>}
        </button>
        {card.reason || (card.subLabel && card.state !== 'queued') ? (
          <p className={`lk-card__sub${card.state === 'blocked' ? ' is-bad' : ''}`}>
            {card.reason ?? card.subLabel}
          </p>
        ) : null}
        {card.state === 'needs-you' && card.askId ? (
          <button
            type="button"
            className="lk-chip is-needs justify-self-start"
            onClick={() => onInspect(`ask:${encodeURIComponent(card.askId as string)}`)}
          >
            <Glyph kind="wait" />
            Answer to resume
            {card.askTitle ? <span className="sr-only">: {card.askTitle}</span> : null}
          </button>
        ) : null}
        {card.chips.length || card.proposed ? (
          <div className="lk-chips">
            {card.proposed ? (
              <button type="button" className="lk-chip is-wait" onClick={() => open()}>
                Proposed: approve to queue
              </button>
            ) : null}
            {card.chips.map((c) => (
              <CardChip key={`${c.kind}:${c.label}`} chip={c} onOpen={open} />
            ))}
          </div>
        ) : null}
        <RowActionBar api={acts} label={card.title} />
      </article>
    </li>
  )
}

function CardChip({ chip, onOpen }: { chip: LaneChip; onOpen: (tab?: string) => void }) {
  const tone = chip.tone ? ` is-${chip.tone}` : ''
  if (chip.kind === 'commit') {
    const [sha, ...rest] = chip.label.split(' ')
    return (
      <button type="button" className={`lk-chip${tone}`} onClick={() => onOpen(':evidence')}>
        <code>{sha}</code> {rest.join(' ')}
      </button>
    )
  }
  return (
    <button
      type="button"
      className={`lk-chip${tone}`}
      onClick={() =>
        onOpen(
          chip.kind === 'evidence' || chip.kind === 'artifacts' // auto-artifacts
            ? ':evidence'
            : chip.kind === 'provenance'
              ? ':origin'
              : '',
        )
      }
    >
      {chip.kind === 'evidence' ? <Glyph kind={chip.tone === 'ok' ? 'ok' : 'run'} /> : null}
      {chip.label}
    </button>
  )
}

// ---- a11y: throttled polite summary, keyboard -----------------------------------------------------

const ANNOUNCE_MS = 10_000

/** One polite summary per tab, at most every 10 s (§1.12). Live lines are not live regions. */
function useAnnouncer(model: LanesModel): string {
  const [text, setText] = useState('')
  const prev = useRef<{ needs: Set<string>; landed: number } | null>(null)
  const last = useRef(0)
  useEffect(() => {
    const needs = new Set(model.cards.filter((c) => c.state === 'needs-you').map((c) => c.id))
    const p = prev.current
    prev.current = { needs, landed: model.counts.landedToday }
    if (!p) return
    const fresh = model.cards.filter((c) => needs.has(c.id) && !p.needs.has(c.id))
    let msg = ''
    if (fresh.length === 1) msg = `${fresh[0]?.title} is waiting for you`
    else if (fresh.length > 1) msg = `${fresh.length} tasks are waiting for you`
    else if (model.counts.landedToday > p.landed) msg = `${model.counts.landedToday} landed today`
    if (!msg || Date.now() - last.current < ANNOUNCE_MS) return
    last.current = Date.now()
    setText(msg)
  }, [model])
  return text
}

/** j/k card, h/l column, [/] lane, a = open the focused card's ask, b = bark box (§1.11). */
function useLaneKeys() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return
      if (!'jkhl[]ab'.includes(e.key) || e.key.length !== 1) return
      if (e.key === 'b') {
        const input = document.querySelector<HTMLElement>('[data-primary-input]:not([disabled])')
        if (input) {
          e.preventDefault()
          input.focus()
        }
        return
      }
      // Cards folded away (idle lanes) are not in the walk.
      const all = [...document.querySelectorAll<HTMLElement>('[data-lane-key]')].filter(
        (el) => !el.closest('details:not([open])'),
      )
      if (!all.length) return
      const pos = (el: HTMLElement) =>
        (el.dataset.laneKey ?? '0:0:0').split(':').map(Number) as [number, number, number]
      const cur = document.activeElement as HTMLElement | null
      const at = cur?.dataset.laneKey ? pos(cur) : null
      if (e.key === 'a') {
        const ask = cur?.closest('[data-card]')?.querySelector<HTMLElement>('.lk-chip.is-needs')
        if (ask) {
          e.preventDefault()
          ask.click()
        }
        return
      }
      const find = (lane: number, col: number, idx: number) =>
        all.find((el) => {
          const [l, c, i] = pos(el)
          return l === lane && c === col && i === idx
        })
      const firstIn = (lane: number, fromCol: number, dir: 1 | -1) => {
        for (let c = fromCol; c >= 0 && c < 4; c += dir) {
          const el = find(lane, c, 0)
          if (el) return el
        }
        return undefined
      }
      let next: HTMLElement | undefined
      if (!at) next = all[0]
      else {
        const [l, c, i] = at
        if (e.key === 'j') next = find(l, c, i + 1)
        if (e.key === 'k') next = find(l, c, i - 1)
        if (e.key === 'l') next = firstIn(l, c + 1, 1)
        if (e.key === 'h') next = firstIn(l, c - 1, -1)
        if (e.key === ']')
          for (let x = l + 1; !next && all.some((el) => pos(el)[0] >= x); x++)
            next = firstIn(x, 0, 1)
        if (e.key === '[') for (let x = l - 1; !next && x >= 0; x--) next = firstIn(x, 0, 1)
      }
      if (next) {
        e.preventDefault()
        next.focus()
        next.scrollIntoView?.({ block: 'nearest' })
      }
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
  }, [])
}
